import { useState } from 'react'
import { Circle, Coffee, Moon, BellOff, RotateCcw } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/clientWeb/components/ui/dropdown-menu'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { useSelfPresence } from '../hooks/useSelfPresence'
import type { PresenceStatus } from '../store/presence.store'
import { statusLabelKey } from '../utils/statusMeta'

interface StatusEntry {
  value: PresenceStatus
  Icon:  typeof Circle
  className: string
}

// Labels come from statusLabelKey — the same words watchers see for this status.
const OPTIONS: StatusEntry[] = [
  { value: 'online', Icon: Circle,  className: 'text-green-500 fill-green-500' },
  { value: 'away',   Icon: Coffee,  className: 'text-amber-500' },
  { value: 'busy',   Icon: Moon,    className: 'text-rose-500' },
  { value: 'dnd',    Icon: BellOff, className: 'text-purple-500' },
]

function findOption(value: PresenceStatus): StatusEntry {
  return OPTIONS.find((o) => o.value === value) ?? OPTIONS[0]
}

/**
 * Compact dropdown for setting / clearing the user's manual presence status.
 * Lives anywhere a small status pill makes sense (settings, avatar menu).
 */
export function PresenceStatusPicker() {
  const t = useTranslation()
  const { me, setStatus, clearStatus } = useSelfPresence()
  const [pending, setPending] = useState(false)

  const displayStatus: PresenceStatus = me?.manualStatus ?? me?.status ?? 'online'
  const opt = findOption(displayStatus)
  const isAuto = me?.manualStatus == null

  const handlePick = async (status: PresenceStatus) => {
    if (pending) return
    setPending(true)
    try { await setStatus(status) } catch { /* hook rolled back */ }
    finally { setPending(false) }
  }

  const handleClear = async () => {
    if (pending) return
    setPending(true)
    try { await clearStatus() } catch { /* rolled back */ }
    finally { setPending(false) }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={pending}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-60"
        >
          <opt.Icon className={`w-3 h-3 ${opt.className}`} />
          <span>
            {isAuto
              ? t('presence.statusAutoSuffix', { status: t(statusLabelKey[opt.value]) })
              : t(statusLabelKey[opt.value])}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onSelect={(e) => { e.preventDefault(); void handlePick(o.value) }}
            className="flex items-center gap-2"
          >
            <o.Icon className={`w-3 h-3 ${o.className}`} />
            <span>{t(statusLabelKey[o.value])}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); void handleClear() }}
          className="flex items-center gap-2 text-muted-foreground"
        >
          <RotateCcw className="w-3 h-3" />
          <span>{t('presence.resetToAuto')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
